import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import UserService from '../services/user'
import Modal from './Modal'


function ManageUsers() {
  const { currentUser } = useUser()
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [pendingInvitations, setPendingInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [updating, setUpdating] = useState(false)
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, user: null })
  const [deleting, setDeleting] = useState(false)
  const [deleteInviteModal, setDeleteInviteModal] = useState({ isOpen: false, invitation: null })
  const [deletingInvite, setDeletingInvite] = useState(false)
  const [resendingInvite, setResendingInvite] = useState(null)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      await Promise.all([loadUsers(), loadPendingInvitations()])
    } catch {
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const loadUsers = async () => {
    try {
      const response = await UserService.getSecurityData()

      if (response.success) {
        // UserService returns { success: true, data: actualData }
        // Backend returns { data: { data: { users: [...], total_count: N, pagination: {...} } } }
        const userData = response.data?.data?.users || []
        setUsers(Array.isArray(userData) ? userData : [])
      } else {
        // UserService returns { success: false, error: errorMessage }
        setError(response.error || 'Failed to load users')
        setUsers([])
      }
    } catch {
      setError('Network error occurred while loading users')
      setUsers([]) // Ensure users is always an array
    }
  }

  const loadPendingInvitations = async () => {
    try {
      const response = await UserService.getPendingInvitations()

      if (response.success) {
        const invitations = response.data?.pending_invitations || []
        setPendingInvitations(Array.isArray(invitations) ? invitations : [])
      } else {
        setError(response.error || 'Failed to load pending invitations')
        setPendingInvitations([])
      }
    } catch {
      setError('Network error occurred while loading pending invitations')
      setPendingInvitations([])
    }
  }

  const handleUpdatePassword = async () => {
    if (!selectedUser) return

    setPasswordError('')
    setUpdating(true)

    try {
      const response = await UserService.updatePassword(selectedUser.email, newPassword, confirmPassword)
      if (response.success) {
        setShowPasswordModal(false)
        setSelectedUser(null)
        setNewPassword('')
        setConfirmPassword('')
        // Optionally show success message or reload users
      } else {
        setPasswordError(response.message || 'Failed to update password')
      }
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to update password')
    } finally {
      setUpdating(false)
    }
  }

  const openPasswordModal = (user) => {
    setSelectedUser(user)
    setShowPasswordModal(true)
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  const closePasswordModal = () => {
    setShowPasswordModal(false)
    setSelectedUser(null)
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  const handleDeleteUser = (user) => {
    setDeleteModal({ isOpen: true, user })
  }

  const confirmDeleteUser = async () => {
    if (!deleteModal.user) return

    setDeleting(true)
    try {
      const response = await UserService.deleteUser(deleteModal.user.id)

      if (response.success) {
        setDeleteModal({ isOpen: false, user: null })
        loadData() // Reload all data
      } else {
        setError(response.error || 'Failed to delete user')
      }
    } catch {
      setError('Network error occurred while deleting user')
    } finally {
      setDeleting(false)
    }
  }

  const cancelDeleteUser = () => {
    setDeleteModal({ isOpen: false, user: null })
  }

  const isInvitationExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date()
  }

  const handleDeleteInvitation = (invitation) => {
    setDeleteInviteModal({ isOpen: true, invitation })
  }

  const confirmDeleteInvitation = async () => {
    if (!deleteInviteModal.invitation) return

    setDeletingInvite(true)
    try {
      const response = await UserService.deleteInvitation(deleteInviteModal.invitation.id)

      if (response.success) {
        setDeleteInviteModal({ isOpen: false, invitation: null })
        loadData() // Reload all data
      } else {
        setError(response.error || 'Failed to delete invitation')
      }
    } catch {
      setError('Network error occurred while deleting invitation')
    } finally {
      setDeletingInvite(false)
    }
  }

  const cancelDeleteInvitation = () => {
    setDeleteInviteModal({ isOpen: false, invitation: null })
  }

  const handleResendInvitation = async (invitation) => {
    setResendingInvite(invitation.id)
    try {
      const response = await UserService.resendInvitation(invitation.id)

      if (response.success) {
        loadData() // Reload all data to show updated invitation
        // Could add a success message here if desired
      } else {
        setError(response.error || 'Failed to resend invitation')
      }
    } catch {
      setError('Network error occurred while resending invitation')
    } finally {
      setResendingInvite(null)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="manage-users-wrapper">
        <div className="manage-users">
          <div className="loading-state">
            <div>Loading users...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="manage-users-wrapper">
      <div className="manage-users">
      <div className="breadcrumb">
        <button
          className="breadcrumb-link"
          onClick={() => navigate('/')}
        >
          Chat Home
        </button>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-current">Manage Users</span>
      </div>

      <div className="page-header">
        <h1>Manage Users</h1>
        <p>Manage users and permissions</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="table-section">
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(users) && users.map((user) => (
                <tr key={user.id}>
                  <td className="user-name">{user.full_name}</td>
                  <td className="user-email">{user.email}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge status-${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(user.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn update-password-btn"
                        onClick={() => openPasswordModal(user)}
                      >
                        Update Password
                      </button>
                      {currentUser?.id !== user.id && (
                        <button
                          className="action-btn delete-user-btn"
                          onClick={() => handleDeleteUser(user)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && !loading && (
            <div className="empty-state">
              <div>No users found</div>
            </div>
          )}
        </div>

        {/* Bottom spacer for breathing room - outside table container */}
        <div style={{ height: '30px' }}></div>
      </div>

      {/* Pending Invitations Section */}
      <div className="table-section">
        <h2>Pending Invitations</h2>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Invited</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(pendingInvitations) && pendingInvitations.map((invitation) => {
                const expired = isInvitationExpired(invitation.expires_at);
                return (
                  <tr key={invitation.id}>
                    <td className="user-name">{invitation.full_name}</td>
                    <td className="user-email">{invitation.email}</td>
                    <td>
                      <span className={`role-badge role-${invitation.role}`}>
                        {invitation.role}
                      </span>
                    </td>
                    <td className="date-cell">{formatDate(invitation.created_at)}</td>
                    <td className="date-cell">{formatDate(invitation.expires_at)}</td>
                    <td>
                      <span className={`status-badge ${expired ? 'status-expired' : 'status-pending'}`}>
                        {expired ? 'Expired' : 'Pending Activation'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {expired && (
                          <button
                            className="action-btn update-password-btn"
                            onClick={() => handleResendInvitation(invitation)}
                            disabled={resendingInvite === invitation.id}
                          >
                            {resendingInvite === invitation.id ? 'Resending...' : 'Resend'}
                          </button>
                        )}
                        <button
                          className="action-btn delete-user-btn"
                          onClick={() => handleDeleteInvitation(invitation)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {pendingInvitations.length === 0 && !loading && (
            <div className="empty-state">
              <div>No pending invitations</div>
            </div>
          )}
        </div>

        {/* Bottom spacer for breathing room */}
        <div style={{ height: '30px' }}></div>
      </div>

      <Modal
        isOpen={showPasswordModal && selectedUser}
        onClose={closePasswordModal}
        title="Update Password"
      >
        <div className="modal-body">
          <p>Update password for <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})</p>

          {passwordError && (
            <div className="error-message">
              {passwordError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={updating}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={closePasswordModal}
            disabled={updating}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleUpdatePassword}
            disabled={updating || !newPassword || !confirmPassword}
          >
            {updating ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={deleteModal.isOpen && deleteModal.user}
        onClose={cancelDeleteUser}
        title="Delete User"
      >
        <div className="modal-body">
          <p>Are you sure you want to delete this user?</p>
          <div className="user-details">
            <strong>{deleteModal.user?.full_name}</strong><br />
            <span className="user-email">{deleteModal.user?.email}</span>
          </div>
          <p className="warning-text">This action cannot be undone.</p>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={cancelDeleteUser}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={confirmDeleteUser}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={deleteInviteModal.isOpen && deleteInviteModal.invitation}
        onClose={cancelDeleteInvitation}
        title="Delete Invitation"
      >
        <div className="modal-body">
          <p>Are you sure you want to delete this invitation?</p>
          <div className="user-details">
            <strong>{deleteInviteModal.invitation?.full_name}</strong><br />
            <span className="user-email">{deleteInviteModal.invitation?.email}</span><br />
            <span className={`role-badge role-${deleteInviteModal.invitation?.role}`}>
              {deleteInviteModal.invitation?.role}
            </span>
          </div>
          <p className="warning-text">
            {deleteInviteModal.invitation && isInvitationExpired(deleteInviteModal.invitation.expires_at)
              ? "This expired invitation will be permanently removed."
              : "The user will no longer be able to activate their account with this invitation."
            }
          </p>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={cancelDeleteInvitation}
            disabled={deletingInvite}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={confirmDeleteInvitation}
            disabled={deletingInvite}
          >
            {deletingInvite ? 'Deleting...' : 'Delete Invitation'}
          </button>
        </div>
      </Modal>
      </div>
    </div>
  )
}

export default ManageUsers